/* All homepage copy, keyed by locale. "Exhale" stays "Exhale"; the real Google
 * reviews stay in their original English. Spanish is neutral Latin American.
 * Daniel: review the Spanish and tweak voice as needed. */

const SAGUARO_QUOTE =
  "Daniel from Exhale built my website for Saguaro Pallets and he did a really great job. He was easy to work with, communicated well, and made the whole process smooth. I didn't have to over explain anything, he just understood what I was going for. The site is professional, runs fast, and makes my business look legit online. If you need a website done right, I'd definitely recommend Exhale.";
const VALEEN_QUOTE =
  "Daniel is great to work with! Very responsive and the quality of work you receive is worth 5 Stars and more.";

export const STRINGS = {
  en: {
    meta: {
      title: "Exhale Software | Custom Automation for Field Service",
      description:
        "Custom dashboards, scheduling, CRM, and invoice automation for field service companies in Arizona. One-time build, you own it, no subscriptions.",
    },
    header: { tagline: "Making Data Yours." },
    hero: {
      tagline: "Making Data Yours.",
      line1: "Software Solution Experts",
      line2: "",
      description:
        "From designing custom builds to architecting Salesforce solutions, we make sure the software works for you and the data is always yours.",
      book: "Book Free Consultation",
      cases: "View Case Studies",
    },
    ownership: {
      eyebrow: "Rent vs. own",
      title: "Should you rent or own?",
      intro:
        "Not all technology is the right technology, and knowing when to use what is the first step to taking your business to the next level. Can't decide on a CRM? Already have one that needs configuration, or a custom solution built around it? Maybe you want software shaped to how your business actually runs, instead of bending it to fit the software. If so, owning might be the move. And if your platforms are already in place, are you getting their full value? Either way, we can help.",
      cta: "Book Free Consultation",
      rightHeader: "Always own your data",
      items: [
        {
          title: "Salesforce",
          text: "From Sales and Service Cloud to Experience Cloud portals, we architect the data model, automation, and integrations the way the platform is meant to scale. We take a platform-first mentality and use advanced Apex, ETL, and LWC to keep orgs safe, optimized, and manageable.",
        },
        {
          title: "HubSpot and other CRMs",
          text: "Whether you are on HubSpot or another CRM, we configure the pipelines, lifecycle stages, and workflows around how you actually sell. We keep the data clean, deduped, and exportable through the API, so your contacts, deals, and history are never locked to one vendor.",
        },
        {
          title: "Fragile spreadsheets",
          text: "Breaking under you? We replace them with a custom build you own outright, or harden and connect what you already run with the validation, backups, and integrations they were always missing. Whatever fits your business and your budget.",
        },
      ],
      closerTitle: "When it's done, you're never trapped.",
      closerSub: "Keep us on a light retainer or just call when you need a hand. ",
      closerStrong: "No contract, ever.",
    },
    proof: {
      eyebrow: "Proof",
      title: "See for yourself.",
      sub: "Real websites for real Arizona businesses. Every one is live, so click through and judge the work yourself.",
      via: "via Google",
      visit: "Visit",
      moreHeader: "Beyond websites",
      sites: [
        {
          name: "Saguaro Pallets",
          url: "https://saguaropallets.com",
          display: "saguaropallets.com",
          shot: "/proof/saguaro-pallets.jpg",
          note: "A fast, professional site that brings them leads.",
          quote: SAGUARO_QUOTE,
          cite: "Saguaro Pallets LLC",
        },
        {
          name: "Guero's Barbershop",
          url: "https://www.guerosbarbershop.com",
          display: "guerosbarbershop.com",
          shot: "/proof/gueros.jpg",
          note: "Services, hours, locations, and a QR menu customers scan right in the chair.",
        },
        {
          name: "Dusteze HVAC",
          url: "https://dustezehvac.com",
          display: "dustezehvac.com",
          shot: "/proof/dusteze.png",
          w: 1891,
          h: 979,
          note: "A clean, modern site for a Phoenix HVAC company, fronted by a full-screen video hero and built to turn visitors into booked calls.",
        },
        {
          name: "Neila Garcia, REALTOR",
          anchor: "Neila Garcia, Bilingual REALTOR",
          url: "https://neilarealtor.com",
          display: "neilarealtor.com",
          shot: "/proof/neila-garcia.png",
          alt: "Homepage of neilarealtor.com, the bilingual REALTOR site built for Neila Garcia",
          w: 1411,
          h: 673,
          meta: "West USA Realty · Greater Phoenix",
          note: [
            "Neila is a bilingual REALTOR® with West USA Realty, working with first-time buyers, families, and investors across Greater Phoenix and the West Valley. The whole site runs in both English and Spanish, so a buyer searching in either language lands on a page written for them instead of a machine translation of someone else's.",
            "Built on Next.js and deployed on Vercel: static pages that load fast on a phone in a driveway, a lead form that reaches her directly, RealEstateAgent structured data so Google understands who she is, and her Google Business Profile set up alongside it so she turns up in local search.",
          ],
        },
      ],
      more: [
        {
          result: "Saved hours of admin, every day.",
          detail:
            "Automated a client's daily report exports into a live chart, so their team stopped pulling and condensing numbers by hand.",
          quote: VALEEN_QUOTE,
          cite: "Valeen P.",
          org: "Pros Tree & Landscape",
        },
        {
          result: "Unblocked a stuck Salesforce org.",
          detail:
            "Came into an org with failing tests that blocked every deployment, fixed the tests, and got them shipping again.",
          cite: "Security device company",
        },
      ],
    },
    about: {
      eyebrow: "Who you're working with",
      name: "Daniel Rangel",
      meta: "Founder, Exhale Software Solutions · Goodyear, Arizona",
      lead: "You work directly with me, start to finish. No account managers, no handoffs, no junior dev you never met doing the real work.",
      body1:
        "I've spent years building for tree service companies, HVAC shops, and landscapers across Arizona, and it's the same story every time: owners juggling five or more subscriptions, crews working off paper, and office staff double-entering data just to keep the day moving.",
      body2:
        "I fix that. Websites, dashboards, scheduling, CRM, and invoicing, built around how your business actually runs. Whatever I build, you keep: your code, your data, your accounts.",
      linkedin: "See my full background on LinkedIn →",
      credLabel: "Certified",
    },
    start: {
      eyebrow: "Start here",
      heading: "Let's get your logo on the board.",
      offer:
        "I keep my client list short on purpose, so the businesses I work with get direct, undivided attention. You tell me how your operation runs; I'll tell you exactly what I'd build and what it costs. Everything I make is yours to keep.",
      availability: "Available in person and onsite · Phoenix, AZ",
      steps: [
        {
          title: "Free consult",
          detail: "We walk through how your business runs today and where it's costing you time. You leave knowing exactly what I'd build, whether or not we work together.",
        },
        {
          title: "I build it",
          detail: "You review as it comes together, and your code and data are yours from day one.",
        },
        {
          title: "It's yours",
          detail: "It goes live, you own it outright, and support is there only if you want it.",
        },
      ],
      call: "Call",
      text: "Text me",
      book: "Book a free consult",
      formLabel: "Or send a message",
      name: "Name",
      namePlaceholder: "Your name",
      contact: "Phone or email",
      contactPlaceholder: "So I can reach you",
      message: "What do you need?",
      messagePlaceholder: "A website, automation, a question...",
      send: "Send message",
      sending: "Sending...",
      invalid: "Add your name and a valid phone number or email.",
      sent: "Got it. I'll get back to you fast.",
      error: "Something went wrong. Call or text me at ",
      reassure: "You own everything outright. No contracts, no subscriptions, no lock-in.",
    },
    footer: {
      tagline: "Making Data Yours.",
      buildTitle: "What I build",
      services: [
        "Websites",
        "Dashboards & reporting",
        "Scheduling & dispatch",
        "CRM & Salesforce",
        "Invoice & payment automation",
      ],
      areasTitle: "Service areas",
      remote: "Remote clients nationwide",
      connectTitle: "Connect",
      call: "Call",
      text: "Text me",
      hours: "Mon-Fri, 8AM-6PM MST",
      credTitle: "Verified Credentials",
      rights: "All rights reserved.",
    },
  },

  es: {
    meta: {
      title: "Exhale Software | Automatización a la Medida para Servicio de Campo",
      description:
        "Tableros, programación, CRM y facturación a la medida para empresas de servicio de campo en Arizona. Un solo pago, tú eres el dueño, sin suscripciones.",
    },
    header: { tagline: "Hacemos tuyos tus datos." },
    hero: {
      tagline: "Hacemos tuyos tus datos.",
      line1: "Expertos en Soluciones de Software",
      line2: "",
      description:
        "Desde diseñar desarrollos a la medida hasta arquitectar soluciones de Salesforce, nos aseguramos de que el software trabaje para ti y de que los datos siempre sean tuyos.",
      book: "Agenda una consulta gratis",
      cases: "Ver casos de éxito",
    },
    ownership: {
      eyebrow: "Rentar o ser dueño",
      title: "¿Rentar o ser dueño?",
      intro:
        "No toda la tecnología es la tecnología correcta, y saber cuándo usar cada cosa es el primer paso para llevar tu negocio al siguiente nivel. ¿No te decides por un CRM? ¿Ya tienes uno que necesita configuración, o una solución a la medida construida a su alrededor? Tal vez quieres software hecho a la forma en que tu negocio realmente trabaja, en lugar de amoldarlo al software. Si es así, quizá lo mejor es ser dueño. Y si ya tienes tus plataformas, ¿les estás sacando todo el provecho? De cualquier forma, te podemos ayudar.",
      cta: "Agenda una consulta gratis",
      rightHeader: "Siempre sé dueño de tus datos",
      items: [
        {
          title: "Salesforce",
          text: "Desde Sales y Service Cloud hasta portales de Experience Cloud, diseñamos el modelo de datos, la automatización y las integraciones tal como la plataforma está hecha para escalar. Adoptamos una mentalidad platform-first y usamos Apex, ETL y LWC avanzados para mantener las orgs seguras, optimizadas y manejables.",
        },
        {
          title: "HubSpot y otros CRMs",
          text: "Ya sea que uses HubSpot u otro CRM, configuramos los pipelines, las etapas del ciclo de vida y los flujos de trabajo según cómo vendes de verdad. Mantenemos los datos limpios, sin duplicados y exportables por la API, para que tus contactos, negocios e historial nunca queden atados a un solo proveedor.",
        },
        {
          title: "Hojas de cálculo frágiles",
          text: "¿Se te rompen a cada rato? Las reemplazamos con algo a la medida del que eres dueño, o reforzamos y conectamos lo que ya usas con la validación, los respaldos y las integraciones que siempre les faltaron. Lo que sea mejor para tu negocio y tu presupuesto.",
        },
      ],
      closerTitle: "Cuando termina, nunca quedas atrapado.",
      closerSub: "Déjanos en un plan de soporte ligero o solo llámanos cuando necesites una mano. ",
      closerStrong: "Sin contratos, nunca.",
    },
    proof: {
      eyebrow: "Pruebas",
      title: "Compruébalo tú mismo.",
      sub: "Sitios web reales para negocios reales de Arizona. Todos están en línea, así que entra y juzga el trabajo por ti mismo.",
      via: "vía Google",
      visit: "Visita",
      moreHeader: "Más allá de los sitios web",
      sites: [
        {
          name: "Saguaro Pallets",
          url: "https://saguaropallets.com",
          display: "saguaropallets.com",
          shot: "/proof/saguaro-pallets.jpg",
          note: "Un sitio rápido y profesional que les trae clientes.",
          quote: SAGUARO_QUOTE,
          cite: "Saguaro Pallets LLC",
        },
        {
          name: "Guero's Barbershop",
          url: "https://www.guerosbarbershop.com",
          display: "guerosbarbershop.com",
          shot: "/proof/gueros.jpg",
          note: "Servicios, horarios, ubicaciones y un menú con código QR que los clientes escanean desde la silla.",
        },
        {
          name: "Dusteze HVAC",
          url: "https://dustezehvac.com",
          display: "dustezehvac.com",
          shot: "/proof/dusteze.png",
          w: 1891,
          h: 979,
          note: "Un sitio limpio y moderno para una empresa de HVAC en Phoenix, encabezado por un hero de video a pantalla completa y hecho para convertir visitantes en llamadas agendadas.",
        },
        {
          name: "Neila Garcia, REALTOR",
          anchor: "Neila Garcia, REALTOR® bilingüe",
          url: "https://neilarealtor.com",
          display: "neilarealtor.com",
          shot: "/proof/neila-garcia.png",
          alt: "Página principal de neilarealtor.com, el sitio bilingüe que construimos para la REALTOR® Neila Garcia",
          w: 1411,
          h: 673,
          meta: "West USA Realty · Área de Phoenix",
          note: [
            "Neila es REALTOR® bilingüe de West USA Realty y trabaja con compradores primerizos, familias e inversionistas en el área de Phoenix y el West Valley. Todo el sitio funciona en inglés y en español, para que quien busque en cualquiera de los dos idiomas llegue a una página escrita para él y no a la traducción automática de otra.",
            "Hecho en Next.js y publicado en Vercel: páginas estáticas que cargan rápido desde el teléfono, un formulario que le llega directo, datos estructurados de RealEstateAgent para que Google entienda quién es, y su perfil de Google Business configurado para que aparezca en las búsquedas locales.",
          ],
        },
      ],
      more: [
        {
          result: "Horas de administración ahorradas, todos los días.",
          detail:
            "Automaticé la exportación diaria de reportes de un cliente en una gráfica en vivo, para que su equipo dejara de sacar y resumir números a mano.",
          quote: VALEEN_QUOTE,
          cite: "Valeen P.",
          org: "Pros Tree & Landscape",
        },
        {
          result: "Desbloqueé una organización de Salesforce atascada.",
          detail:
            "Entré a una organización con pruebas fallidas que bloqueaban cada despliegue, arreglé las pruebas y la dejé desplegando de nuevo.",
          cite: "Empresa de dispositivos de seguridad",
        },
      ],
    },
    about: {
      eyebrow: "Con quién trabajas",
      name: "Daniel Rangel",
      meta: "Fundador, Exhale Software Solutions · Goodyear, Arizona",
      lead: "Trabajas directamente conmigo, de principio a fin. Sin gerentes de cuenta, sin pasar de mano en mano, sin un programador junior que nunca conociste haciendo el trabajo de verdad.",
      body1:
        "Llevo años construyendo para empresas de poda de árboles, talleres de HVAC y jardineros en todo Arizona, y siempre es la misma historia: dueños haciendo malabares con cinco o más suscripciones, cuadrillas trabajando en papel y el personal de oficina capturando los mismos datos dos veces solo para sacar el día.",
      body2:
        "Yo resuelvo eso. Sitios web, tableros, programación, CRM y facturación, construidos según cómo funciona tu negocio de verdad. Sea lo que sea que construya, te lo quedas: tu código, tus datos, tus cuentas.",
      linkedin: "Ve mi experiencia completa en LinkedIn →",
      credLabel: "Certificado",
    },
    start: {
      eyebrow: "Empieza aquí",
      heading: "Pongamos tu logo en el tablero.",
      offer:
        "Mantengo mi lista de clientes corta a propósito, para que los negocios con los que trabajo reciban atención directa y completa. Tú me dices cómo opera tu negocio; yo te digo exactamente qué construiría y cuánto cuesta. Todo lo que hago es tuyo.",
      availability: "Disponible en persona y en sitio · Phoenix, AZ",
      steps: [
        {
          title: "Consulta gratis",
          detail: "Recorremos cómo funciona tu negocio hoy y dónde te está costando tiempo. Sales sabiendo exactamente qué construiría, trabajemos juntos o no.",
        },
        {
          title: "Lo construyo",
          detail: "Lo revisas mientras va tomando forma, y tu código y tus datos son tuyos desde el primer día.",
        },
        {
          title: "Es tuyo",
          detail: "Sale en vivo, eres dueño por completo, y el soporte está ahí solo si lo quieres.",
        },
      ],
      call: "Llama",
      text: "Escríbeme",
      book: "Agenda una consulta gratis",
      formLabel: "O envía un mensaje",
      name: "Nombre",
      namePlaceholder: "Tu nombre",
      contact: "Teléfono o correo",
      contactPlaceholder: "Para poder contactarte",
      message: "¿Qué necesitas?",
      messagePlaceholder: "Un sitio web, automatización, una pregunta...",
      send: "Enviar mensaje",
      sending: "Enviando...",
      invalid: "Agrega tu nombre y un teléfono o correo válido.",
      sent: "¡Listo! Te responderé pronto.",
      error: "Algo salió mal. Llámame o escríbeme al ",
      reassure: "Eres dueño de todo por completo. Sin contratos, sin suscripciones, sin ataduras.",
    },
    footer: {
      tagline: "Hacemos tuyos tus datos.",
      buildTitle: "Lo que construyo",
      services: [
        "Sitios web",
        "Tableros y reportes",
        "Programación y despacho",
        "CRM y Salesforce",
        "Facturación y pagos automatizados",
      ],
      areasTitle: "Áreas de servicio",
      remote: "Clientes remotos en todo el país",
      connectTitle: "Contacto",
      call: "Llama",
      text: "Escríbeme",
      hours: "Lun-Vie, 8AM-6PM MST",
      credTitle: "Credenciales verificadas",
      rights: "Todos los derechos reservados.",
    },
  },
};
